/**
 * Compliance Calendar guide anchors: the controls a person uses to add a
 * task, on the calendar and in the task drawer, and how the console knows
 * each one is done. Kernel: shared/guideAnchor.ts; registry and catalogue:
 * shared/guideAnchorRegistry.ts. The page marks each control with
 * guideAnchor(name) (frontend/src/platform/guide/guideAnchor.js); the drawer
 * marks its controls only when it creates, so opening or editing a task
 * ticks nothing.
 *
 * Saving is read back from the task list the page already loads, for last,
 * this and next year together (frontend/src/features/compliance_calendar/
 * client.js), whichever year the calendar shows: a task series listed there
 * that was not listed when the tour started is the task they added, and
 * deleting it (the series is archived) un-ticks the step. The drawer's own
 * steps are observed on the page (the drawer open, the title filled). The category, recurrence, anchor month and
 * start year already hold usable defaults, so they are not steps.
 */

import { defineGuideAnchors, defineGuideStateReads, type GuidePage } from '../guideAnchor'
import { contracts } from './compliance_calendar.contracts'
import { identity } from './compliance_calendar'

interface ListedOccurrence {
  readonly series_id?: unknown
}

function listed(data: unknown): readonly ListedOccurrence[] {
  const occurrences = (data as { occurrences?: unknown } | null | undefined)?.occurrences
  return Array.isArray(occurrences) ? occurrences.filter((occurrence): occurrence is ListedOccurrence => Boolean(occurrence) && typeof occurrence === 'object') : []
}

export const guideStateReads = defineGuideStateReads(identity, contracts, {
  'compliance_calendar.list.created': {
    label: 'A task that is new since the guide started',
    contract: 'listTasks',
    items: (data) => [...new Set(listed(data)
      .filter((occurrence) => occurrence.series_id != null)
      .map((occurrence) => String(occurrence.series_id)))],
  },
})

const page: GuidePage = { path: '/compliance/calendar' }

export const guideAnchors = defineGuideAnchors(identity, guideStateReads, {
  'compliance_calendar.list.add': {
    label: 'Add task',
    page,
    completion: { kind: 'visible', anchor: 'compliance_calendar.create.dialog' },
  },
  'compliance_calendar.create.dialog': {
    label: 'Add Task drawer',
    page,
    completion: { kind: 'visible' },
  },
  'compliance_calendar.create.title': {
    label: 'Title',
    page,
    completion: { kind: 'filled' },
  },
  'compliance_calendar.create.save': {
    label: 'Save',
    page,
    completion: { kind: 'state', read: 'compliance_calendar.list.created' },
  },
})
